import { useEffect } from 'react';
import {
  Button,
  CircularProgress,
  Typography,
  makeStyles,
} from '@material-ui/core';
import CheckIcon from '@material-ui/icons/Check';
import CloseIcon from '@material-ui/icons/Close';
import RemoveIcon from '@material-ui/icons/Remove';
import { Link, LinkButton, Progress } from '@backstage/core-components';
import { useRouteRef } from '@backstage/core-plugin-api';
import { useEntityRefLink } from '@backstage/plugin-catalog-react';
import { scaffolderPlugin } from '@backstage/plugin-scaffolder';
import {
  useTaskEventStream,
  type ScaffolderStep,
} from '@backstage/plugin-scaffolder-react';
import { PanelShell, usePanelStyles } from './panelParts';

/**
 * A run, in the panel that started it (DX-650): each step as it goes, then what
 * came of it — the template's links when it succeeded, the step that stopped it
 * and why when it did not. The full log stays one link away, on the engine's
 * own page for the run.
 */

const useStyles = makeStyles(theme => ({
  steps: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.75),
    '& li': { display: 'flex', alignItems: 'center', gap: theme.spacing(1.5) },
  },
  mark: {
    flex: 'none',
    display: 'grid',
    placeItems: 'center',
    width: 24,
    height: 24,
    boxSizing: 'border-box',
    borderRadius: '50%',
    '& svg': { fontSize: 15 },
  },
  done: { background: 'var(--ui-nav-active-bg)', color: 'var(--ui-primary)' },
  failed: { background: 'var(--ui-danger-border)', color: '#ffffff' },
  waiting: { border: '1px dashed var(--color-brand-300)' },
  skipped: {
    border: '1px solid var(--ui-border)',
    color: 'var(--ui-text-muted)',
  },
  notRun: { color: 'var(--ui-text-muted)' },
  ready: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    padding: theme.spacing(2),
    borderRadius: 8,
    border: '1px solid var(--color-brand-200)',
    background: 'var(--ui-nav-hover-bg)',
  },
  readyHead: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.25),
    fontSize: '1rem',
    fontWeight: 600,
  },
  links: { display: 'flex', flexWrap: 'wrap', gap: theme.spacing(1) },
  stopped: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    padding: theme.spacing(2),
    borderRadius: 8,
    border: '1px solid var(--ui-danger-border)',
    background: 'var(--ui-danger-bg)',
    fontSize: '0.875rem',
    lineHeight: 1.5,
  },
  stoppedHead: { color: 'var(--ui-danger-text)', fontSize: '1rem' },
  reason: {
    fontFamily: 'var(--font-mono)',
    fontSize: '0.8125rem',
    overflowWrap: 'anywhere',
  },
  aside: { margin: 0, fontSize: '0.8125rem', color: 'var(--ui-text-muted)' },
  unseen: {
    position: 'absolute',
    width: 1,
    height: 1,
    overflow: 'hidden',
    clip: 'rect(0 0 0 0)',
    whiteSpace: 'nowrap',
  },
}));

/** The message of a step's last log line: the line after its timestamp. */
const reasonIn = (logs?: string[]) =>
  logs?.[logs.length - 1]?.replace(/^\S+\s/, '').split('\n')[0];

const StepMark = ({ status }: { status?: ScaffolderStep['status'] }) => {
  const classes = useStyles();
  if (status === 'completed') {
    return (
      <span className={`${classes.mark} ${classes.done}`} aria-hidden>
        <CheckIcon />
      </span>
    );
  }
  if (status === 'processing') {
    return (
      <span className={classes.mark} aria-hidden>
        <CircularProgress size={20} thickness={5} />
      </span>
    );
  }
  if (status === 'failed') {
    return (
      <span className={`${classes.mark} ${classes.failed}`} aria-hidden>
        <CloseIcon />
      </span>
    );
  }
  if (status === 'skipped' || status === 'cancelled') {
    return (
      <span className={`${classes.mark} ${classes.skipped}`} aria-hidden>
        <RemoveIcon />
      </span>
    );
  }
  return <span className={`${classes.mark} ${classes.waiting}`} aria-hidden />;
};

const STATUS_WORDS: Partial<Record<ScaffolderStep['status'], string>> = {
  completed: 'done',
  processing: 'running',
  failed: 'failed',
  skipped: 'skipped',
  cancelled: 'cancelled',
};

type Props = {
  taskId: string;
  title: string;
  titleId: string;
  /** What the run is about — the repository, where the form picked one. */
  subject: string;
  onClose: () => void;
  onFinished?: () => void;
  onBackToForm: () => void;
  onAnother: () => void;
};

export const RunPhase = ({
  taskId,
  title,
  titleId,
  subject,
  onClose,
  onFinished,
  onBackToForm,
  onAnother,
}: Props) => {
  const panel = usePanelStyles();
  const classes = useStyles();
  const stream = useTaskEventStream(taskId);
  const taskLink = useRouteRef(scaffolderPlugin.routes.ongoingTask);
  const entityLink = useEntityRefLink();

  const steps = stream.task?.spec.steps ?? [];
  const failedStep = steps.find(
    step => stream.steps[step.id]?.status === 'failed',
  );
  let outcome: 'running' | 'done' | 'stopped' | 'cancelled' = 'running';
  if (failedStep || (stream.completed && stream.error)) outcome = 'stopped';
  else if (stream.cancelled) outcome = 'cancelled';
  else if (stream.completed) outcome = 'done';
  const reason = failedStep
    ? reasonIn(stream.stepLogs[failedStep.id])
    : stream.error?.message;

  const finished = outcome !== 'running';
  useEffect(() => {
    if (finished) onFinished?.();
  }, [finished, onFinished]);

  let status = <p className={panel.status}>Creating {subject} …</p>;
  if (outcome === 'done') {
    status = <p className={panel.status}>Created {subject}</p>;
  } else if (outcome === 'stopped') {
    status = (
      <p className={`${panel.status} ${panel.stopped}`}>
        {failedStep ? `Stopped at “${failedStep.name}”` : 'Stopped'}
      </p>
    );
  } else if (outcome === 'cancelled') {
    status = <p className={panel.status}>Cancelled</p>;
  }

  const links = (stream.output?.links ?? []).flatMap(link => {
    const to = link.url ?? (link.entityRef ? entityLink(link.entityRef) : '');
    return to ? [{ to, title: link.title ?? to }] : [];
  });

  let foot = (
    <Button variant="outlined" onClick={onClose}>
      Close
    </Button>
  );
  if (outcome === 'done') {
    foot = (
      <>
        <Button variant="outlined" onClick={onAnother}>
          Create another
        </Button>
        <Button variant="outlined" onClick={onClose}>
          Close
        </Button>
      </>
    );
  } else if (outcome === 'stopped' || outcome === 'cancelled') {
    foot = (
      <>
        <Button variant="outlined" onClick={onClose}>
          Close
        </Button>
        <Button variant="contained" color="primary" onClick={onBackToForm}>
          Back to the form
        </Button>
      </>
    );
  }

  return (
    <PanelShell
      titleId={titleId}
      title={title}
      onClose={onClose}
      status={status}
      foot={foot}
    >
      {outcome === 'done' && (
        <div role="status" className={classes.ready}>
          <span className={classes.readyHead}>
            <span className={`${classes.mark} ${classes.done}`} aria-hidden>
              <CheckIcon />
            </span>
            {subject} is ready
          </span>
          {links.length > 0 && (
            <span className={classes.links}>
              {links.map((link, index) => (
                <LinkButton
                  key={link.to}
                  to={link.to}
                  variant={index === 0 ? 'contained' : 'outlined'}
                  color="primary"
                >
                  {link.title}
                </LinkButton>
              ))}
            </span>
          )}
          {(stream.output?.text ?? []).map(text => (
            <Typography key={text.title ?? text.content} variant="body2">
              {text.title && <strong>{text.title}: </strong>}
              {text.content}
            </Typography>
          ))}
        </div>
      )}

      {outcome === 'stopped' && (
        <div role="alert" className={classes.stopped}>
          <strong className={classes.stoppedHead}>
            {failedStep
              ? `“${failedStep.name}” failed`
              : 'The run stopped before it finished'}
          </strong>
          {reason && <span className={classes.reason}>{reason}</span>}
          <span>
            The steps after it did not run. Everything you entered is kept, so
            you can go back to the form and start again.
          </span>
        </div>
      )}

      {stream.loading && steps.length === 0 ? (
        <Progress />
      ) : (
        <ol aria-label="Steps of the run" className={classes.steps}>
          {steps.map(step => {
            const state = stream.steps[step.id]?.status;
            const notRun =
              outcome !== 'running' && (!state || state === 'open');
            const word = notRun ? 'not run' : state && STATUS_WORDS[state];
            return (
              <li key={step.id} className={notRun ? classes.notRun : undefined}>
                <StepMark status={state} />
                <span>
                  {step.name}
                  {/* The state in words too: the marks are drawn, not read. */}
                  <span className={classes.unseen}>
                    {word ? ` — ${word}` : ''}
                  </span>
                </span>
              </li>
            );
          })}
        </ol>
      )}

      {outcome === 'running' && (
        <p className={classes.aside}>
          You can close this panel. The run carries on, and Your runs on this
          page lists it.
        </p>
      )}
      <Link to={taskLink({ taskId })}>Show the full log</Link>
    </PanelShell>
  );
};
